import { useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { HomeLazySection } from '../HomeLazySection';
import { HomeNewsletterForm } from '../HomeNewsletterForm';
import { homeCta } from '../homeCtaStyles';
import { HOME_FAQ_GROUPS } from '../homeContent';

type FaqEntry = {
  id: string;
  tag: string;
  q: string;
  a: string;
};

function faqItemId(groupId: string, question: string) {
  return `${groupId}::${question}`;
}

const DEFAULT_OPEN_FAQ_ID = faqItemId(
  HOME_FAQ_GROUPS[0].id,
  HOME_FAQ_GROUPS[0].items[0].q,
);

export function HomeFaqNewsletterSection() {
  const baseId = useId();
  const [openItemId, setOpenItemId] = useState(DEFAULT_OPEN_FAQ_ID);

  const allFaqs = useMemo<FaqEntry[]>(
    () =>
      HOME_FAQ_GROUPS.flatMap((group) =>
        group.items.map((item) => ({
          id: faqItemId(group.id, item.q),
          tag: group.label,
          q: item.q,
          a: item.a,
        })),
      ),
    [],
  );

  const [leftColumn, rightColumn] = useMemo(() => {
    const mid = Math.ceil(allFaqs.length / 2);
    return [allFaqs.slice(0, mid), allFaqs.slice(mid)];
  }, [allFaqs]);

  const selectItem = (id: string) => {
    setOpenItemId(id);
  };

  const renderColumn = (items: FaqEntry[]) => (
    <div className="home-faq-stack">
      {items.map((item) => {
        const isOpen = openItemId === item.id;
        const panelId = `${baseId}-${item.id}`;

        return (
          <article key={item.id} className={`home-faq__item ${isOpen ? 'is-open' : ''}`}>
            <button
              type="button"
              className="home-faq__trigger"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => selectItem(item.id)}
            >
              <span className="home-faq__question-wrap">
                <span className="home-faq__tag">{item.tag}</span>
                <span className="home-faq__question">{item.q}</span>
              </span>
              <ChevronDownIcon className="home-faq__chevron" aria-hidden />
            </button>
            <div
              id={panelId}
              aria-hidden={!isOpen}
              className={`home-faq__panel ${isOpen ? 'is-open' : ''}`}
            >
              <div className="home-faq__panel-inner">
                <p>{item.a}</p>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );

  return (
    <HomeLazySection
      className="home-faq-section relative py-12 md:py-16 pb-28 md:pb-16"
      placeholderMinHeight="420px"
      lazy
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-xl">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-purple-100 bg-white px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-700">
              <ShieldCheckIcon className="h-3.5 w-3.5" />
              Before you buy
            </p>
            <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">
              Questions buyers ask most.
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Tap any question to switch—the first answer opens by default.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Link to="/shop" className={`${homeCta.primary} !px-5 !py-2.5 !text-xs`}>
              Shop now
              <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" />
            </Link>
            <Link to="/about#faq" className={`${homeCta.secondary} !px-5 !py-2.5 !text-xs`}>
              More on About
              <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-6 lg:items-start">
          <div className="lg:col-span-8">
            <div className="home-faq-columns">
              {renderColumn(leftColumn)}
              {renderColumn(rightColumn)}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="home-faq-aside lg:sticky lg:top-24">
              <div className="flex items-center gap-2">
                <SparklesIcon className="h-4 w-4 text-purple-600" aria-hidden />
                <p className="text-xs font-extrabold text-slate-900">Drop alerts</p>
              </div>
              <p className="mt-1 text-xs text-slate-600">
                Early access and restock pings—unsubscribe anytime.
              </p>
              <div className="mt-3">
                <HomeNewsletterForm compact />
              </div>
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-200/80 pt-3 text-xs font-semibold">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1 text-slate-700 hover:text-[#3B3B98]"
                >
                  <EnvelopeIcon className="h-3.5 w-3.5" />
                  Support
                </Link>
                <Link to="/track-order" className="text-slate-700 hover:text-[#3B3B98]">
                  Track order
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </HomeLazySection>
  );
}
